/**
 * BlockCustomerSubview — wallet-portal Orders drawer block-customer step.
 *
 * Renders IN-PLACE inside the Order-details drawer (view_order/index.tsx swaps
 * this in for the normal drawer content while a block confirmation is up) —
 * same step-transition pattern as RefundOrderSubview. `onBack` returns to the
 * Order details view within the same drawer; `onClose` dismisses the whole
 * drawer, matching the drawer's own close button.
 *
 * Unlike RefundOrderSubview, this view is purely presentational — it has no
 * form fields to validate and no quote to fetch, so view_order/index.tsx owns
 * the actual apiOrderBlockService.blockCustomer call and all success/error
 * handling (including swapping back to Order details on error) rather than
 * this component managing its own submit lifecycle.
 */

import { FC } from 'react';
import { Icon } from '@components/icons';
import styles from './block_customer_subview.module.scss';

export interface BlockCustomerSubviewProps {
    customerName: string;
    isSubmitting: boolean;
    /** Return to the Order details view within the same drawer. */
    onBack: () => void;
    /** Dismiss the entire Order details drawer. */
    onClose: () => void;
    /** Called when the user confirms — view_order/index.tsx performs the
     *  actual API call and decides what happens next. */
    onConfirm: () => void;
}

const BlockCustomerSubview: FC<BlockCustomerSubviewProps> = ({
    customerName,
    isSubmitting,
    onBack,
    onClose,
    onConfirm
}) => {
    return (
        <div className={styles.bc_modal}>
            {/* Header: back arrow returns to Order details, X closes the whole drawer */}
            <div className={styles.bc_header}>
                <button
                    type="button"
                    className={styles.bc_back}
                    onClick={onBack}
                    aria-label="Back to order details">
                    <Icon name="arrow_left" width={20} height={20} />
                </button>
                <p className={styles.bc_title}>Block customer</p>
                <button
                    type="button"
                    className={styles.bc_close}
                    onClick={onClose}
                    aria-label="Close">
                    <Icon name="close" width={24} height={24} />
                </button>
            </div>
            <div className={styles.bc_divider} />

            <div className={styles.bc_content}>
                <p className={styles.bc_question}>
                    Are you sure you want to block {customerName}?
                </p>
                <div className={styles.bc_warning_banner}>
                    <Icon name="warning" width={20} height={20} className={styles.bc_warning_icon} />
                    <p className={styles.bc_warning_text}>
                        {customerName} won&apos;t be able to place new orders with your business.
                        You can unblock them at any time from Manage contacts → Blocked.
                    </p>
                </div>
            </div>

            <div className={styles.bc_footer}>
                <button
                    type="button"
                    className={styles.bc_btn_cancel}
                    onClick={onBack}
                    disabled={isSubmitting}>
                    Cancel
                </button>
                <button
                    type="button"
                    className={styles.bc_btn_confirm}
                    disabled={isSubmitting}
                    onClick={onConfirm}
                    aria-busy={isSubmitting}>
                    {isSubmitting ? 'Blocking…' : 'Block customer'}
                </button>
            </div>
        </div>
    );
};

export default BlockCustomerSubview;
